import { todayBrazilISO } from '@/lib/brazilTime';

export interface EnsinoBanner {
  id: string;
  image_url: string;
  link_url: string | null;
  button_label: string | null;
  button_color: string | null;
  position: number;
  is_active: boolean;
  created_at: string;
}

export interface EnsinoTrack {
  id: string;
  name: string;
  position: number;
  created_at: string;
  min_days_after_registration: number | null;
}

export interface EnsinoCourse {
  id: string;
  title: string;
  slug: string;
  description: string | null;
  thumbnail_url: string | null;
  sales_url: string | null;
  allowed_roles: string[];
  is_published: boolean;
  available_at: string | null;
  min_days_after_registration: number | null;
  created_at: string;
  updated_at: string;
}

/**
 * Check if user has enough days as a student to access an item.
 * Items can be tracks, courses, modules or lessons — anything carrying
 * `min_days_after_registration`. The reference date is when the user became
 * a student (`profiles.student_since`); for backwards compatibility we accept
 * a fallback like `profiles.created_at`.
 *
 * Backwards-compatible alias `hasRegistrationAccess` is kept below.
 */
export function hasStudentDaysAccess(
  item: { min_days_after_registration: number | null } | null | undefined,
  studentSince: string | null | undefined,
): boolean {
  if (!item || !item.min_days_after_registration) return true;
  if (!studentSince) return false;
  const msElapsed = Date.now() - new Date(studentSince).getTime();
  const msRequired = item.min_days_after_registration * 24 * 60 * 60 * 1000;
  return msElapsed >= msRequired;
}

export function getStudentDaysRemaining(
  item: { min_days_after_registration: number | null } | null | undefined,
  studentSince: string | null | undefined,
): number {
  if (!item || !item.min_days_after_registration || !studentSince) return 0;
  const msElapsed = Date.now() - new Date(studentSince).getTime();
  const msRequired = item.min_days_after_registration * 24 * 60 * 60 * 1000;
  const msRemaining = msRequired - msElapsed;
  return msRemaining <= 0 ? 0 : Math.ceil(msRemaining / (24 * 60 * 60 * 1000));
}

// Backwards-compatible aliases (older call sites still pass userCreatedAt)
export const hasRegistrationAccess = (
  course: { min_days_after_registration: number | null },
  studentSinceOrCreatedAt: string | null | undefined,
) => hasStudentDaysAccess(course, studentSinceOrCreatedAt);

export const getDaysUntilAccess = (
  course: { min_days_after_registration: number | null },
  studentSinceOrCreatedAt: string | null | undefined,
) => getStudentDaysRemaining(course, studentSinceOrCreatedAt);

export interface EnsinoTrackCourse {
  id: string;
  track_id: string;
  course_id: string;
  position: number;
}

export interface EnsinoModule {
  id: string;
  course_id: string;
  title: string;
  slug: string;
  description: string | null;
  thumbnail_url: string | null;
  is_published: boolean;
  available_at: string | null;
  position: number;
  has_exam: boolean;
  exam_required_to_advance: boolean;
  created_at: string;
  min_days_after_registration: number | null;
}

export interface EnsinoLesson {
  id: string;
  module_id: string;
  course_id: string;
  title: string;
  slug: string;
  description: string | null;
  video_url: string | null;
  duration_seconds: number | null;
  is_published: boolean;
  available_at: string | null;
  position: number;
  requires_previous: boolean;
  comments_enabled: boolean;
  created_at: string;
  updated_at: string;
  min_days_after_registration: number | null;
}

export interface EnsinoProgress {
  id: string;
  user_id: string;
  lesson_id: string;
  completed: boolean;
  completed_at: string | null;
  created_at: string;
}

export interface EnsinoRating {
  id: string;
  user_id: string;
  lesson_id: string;
  rating: number;
  created_at: string;
}

export interface EnsinoComment {
  id: string;
  user_id: string;
  lesson_id: string;
  parent_id: string | null;
  content: string;
  created_at: string;
}

export interface EnsinoCommentLike {
  id: string;
  user_id: string;
  comment_id: string;
  created_at: string;
}

export interface EnsinoGamificationConfig {
  id: string;
  action: string;
  points: number;
  enabled: boolean;
}

// Derived types for frontend use
export interface TrackWithCourses extends EnsinoTrack {
  courses: (EnsinoCourse & { track_position: number })[];
}

export interface ModuleWithLessons extends EnsinoModule {
  lessons: EnsinoLesson[];
}

export interface CourseWithModules extends EnsinoCourse {
  modules: ModuleWithLessons[];
}

/**
 * Video embed allowlist domains
 */
export const ALLOWED_EMBED_DOMAINS = [
  'youtube.com',
  'www.youtube.com',
  'youtu.be',
  'youtube-nocookie.com',
  'www.youtube-nocookie.com',
  'play.tynk.ai',
  'b-cdn.net',
  'mediadelivery.net',
  'player.mediadelivery.net',
  'iframe.mediadelivery.net',
];

/**
 * Check if item is available (published + date check)
 */
export function isAvailable(item: { is_published: boolean; available_at: string | null }): boolean {
  if (!item.is_published) return false;
  if (!item.available_at) return true;
  // Parse date-only string without UTC shift
  const datePart = item.available_at.split('T')[0];
  return datePart <= todayBrazilISO();
}

/**
 * Check if user role has access to a course
 */
export function hasRoleAccess(course: EnsinoCourse, userRole: string): boolean {
  // Admin and staff always have access
  if (userRole === 'admin' || userRole === 'staff') return true;
  return course.allowed_roles.includes(userRole);
}
